import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Buttons, FlexContainer, Spinner, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { showModalAction } from 'Store/modules/app/actions';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { userAuthSelector } from 'Store/modules/auth/selectors';
import useTranslation from 'Hooks/useTranslation';
import { useGetUserOneTimeProducts, useGetUserSatInfo } from 'Containers/PreviousYears/hooks';
import FormEfirma from 'Components/FormEfirma';
import { EFIRMA_MODAL } from 'Constants/modals';
import { setBodyOverflow } from 'Styles/utils/utils';
import { TAXDOWN_ASSETS_DOMAIN } from 'Constants/domains';
import { routes } from 'Constants/routes';
import { WHAT_IS_EFIRMA_URL } from 'Constants/helpsUrl';
import { openLinkInNewTab } from 'Utils/links';
import * as Styled from './styles';

const PreviousYearsWorking = (): JSX.Element => {
  const info = useSelector(routeStateSelector);
  const userAuth = useSelector(userAuthSelector);
  const oneTimeProduct = useGetUserOneTimeProducts();
  const { t } = useTranslation(locizeNS.PY);
  const { data, isLoading, refetch, isFetching } = useGetUserSatInfo();
  const dispatch = useDispatch();
  const userHasEfirma = data && data.efirmaIsValid;
  const EfirmaBullets = [
    t('First.efirma.bullet'),
    t('Second.efirma.bullet'),
    t('Third.efirma.bullet'),
  ];

  const PayUpFrontBullets = [
    t('First.payUpFront.bullet'),
    t('Second.payUpFront.bullet'),
    t('Third.payUpFront.bullet'),
  ];

  const NotEfirmaBullets = [
    t('First.notEfirma.bullet'),
    t('Second.notEfirma.bullet'),
    t('Third.notEfirma.bullet'),
  ];

  useEffect(() => {
    if (info?.isDashboardUser) {
      setBodyOverflow();
      dispatch(
        showModalAction({
          options: {
            type: EFIRMA_MODAL,
            data: {
              refetch,
            },
          },
          Component: FormEfirma,
        })
      );
    }
  }, [info]);

  const handleClick = (): void => {
    setBodyOverflow();
    dispatch(
      showModalAction({
        options: {
          type: EFIRMA_MODAL,
          data: {
            refetch,
          },
        },
        Component: FormEfirma,
      })
    );
  };

  const isPayUpFront = oneTimeProduct === 'card';

  const urlImg = !userHasEfirma
    ? `${TAXDOWN_ASSETS_DOMAIN}/illustrations/default/add_doc_s.png`
    : isPayUpFront
      ? `${TAXDOWN_ASSETS_DOMAIN}/illustrations/default/point_s.png`
      : `${TAXDOWN_ASSETS_DOMAIN}/illustrations/default/done_s.png`;

  const handlePayUpFront = (): void => {
    openLinkInNewTab(
      `https://airtable.com/app9VSgzvpyXZ1NtW/shr7xIbbrDk0t5JM7?prefill_userId=${userAuth?.id}&prefill_Datos+necesarios=&prefill_recordIdFormulario=rec7Qu4SBTINqUwPU&hide_userId=true&hide_recordIdFormulario=true&hide_Datos+necesarios=true`
    );
  };

  return (
    <Styled.ThreeColLayout>
      <Styled.TitleContainer type="headline" size="S_L_S" color="dark">
        {t('PreviousYears.title')}
      </Styled.TitleContainer>
      <FlexContainer direction="column" fullWidth gap="dimension_16">
        <Styled.Title type="headline" color="dark" size="XS_M_S">
          {t('PreviousYears.title')}
        </Styled.Title>
        <Styled.ContainerInProgress>
          <FlexContainer direction="column" align="center" gap="dimension_16" justify="center">
            {isLoading || isFetching ? (
              <Spinner size="M" justify="center" align="center" />
            ) : (
              <>
                <img src={urlImg} alt="workInProgress" />
                <Text align="center" type="fds_text_bold_L" color="dark_500">
                  {t('PreviousYears.inprogress.title', {
                    context: (!userHasEfirma && 'efirma.request') || (isPayUpFront && 'payUpFront'),
                  })}
                </Text>
                <Styled.DescriptionContainer>
                  <Text align="center" type="fds_paragraph_medium_S" color="dark_500">
                    {t('PreviousYears.inprogress.description', {
                      context:
                        (!userHasEfirma && 'efirma.request') || (isPayUpFront && 'payUpFront'),
                    })}
                  </Text>
                </Styled.DescriptionContainer>
                {!userHasEfirma && (
                  <FlexContainer direction="column" gap="dimension_16">
                    <Buttons.Classic
                      mode="primary"
                      size="s"
                      label={t('Efirma.button.upload')}
                      onClick={handleClick}
                    />
                    <Buttons.Classic
                      mode="pro_light"
                      size="s"
                      label={t('Efirma.button.help')}
                      onClick={() => history.push(routes.PREVIOUS_YEARS_EFIRMA_TYPE_FORM)}
                    />
                  </FlexContainer>
                )}
                <Styled.InformativeInfo
                  bullets={
                    !userHasEfirma
                      ? NotEfirmaBullets
                      : isPayUpFront
                        ? PayUpFrontBullets
                        : EfirmaBullets
                  }
                  description={t('Efirma.description.help', {
                    context: userHasEfirma && 'hasEfirma',
                  })}
                  helpLink={
                    !userHasEfirma
                      ? {
                          title: t('Efirma.button.help.informative'),
                          url: WHAT_IS_EFIRMA_URL,
                        }
                      : undefined
                  }
                  mode="light"
                  noClose
                  title={t('Efirma.title.help', {
                    context: userHasEfirma && 'hasEfirma',
                  })}
                />
                {isPayUpFront && userHasEfirma && (
                  <Buttons.Classic
                    mode="primary"
                    size="s"
                    label={t('PreviousYears.payUpFront.button')}
                    onClick={handlePayUpFront}
                  />
                )}
              </>
            )}
          </FlexContainer>
        </Styled.ContainerInProgress>
      </FlexContainer>
    </Styled.ThreeColLayout>
  );
};

export default PreviousYearsWorking;
